import { useClientTheme } from '@/hooks/client/useClientTheme';

/** Role of the last login (the session isn't checked yet while this shows); same source as EventRequestsWarmup. */
function storedRole(): string | undefined {
  try {
    return JSON.parse(localStorage.getItem('user') ?? 'null')?.role;
  } catch {
    return undefined;
  }
}

/**
 * Neutral page skeleton for the event route. The auth check, the lazy route chunk, the event request and the lazy event
 * UI all show this same one, so the viewer sees a single loader.
 *
 * A client is about to get the Cotiza v2 app, which paints on its own themed background: this renders inside the same
 * `.client-app` + `data-theme` so there is no light-to-dark flash when the app mounts. Anyone else (admins land on the
 * legacy dashboard) keeps the legacy look. Imported from the eager route table, so it only pulls in the small theme hook.
 */
export function EventLoader() {
  const { resolved } = useClientTheme();
  if (storedRole() === 'client') {
    return (
      <div
        role="status"
        data-theme={resolved}
        className="client-app client-app-bg min-h-dvh font-[family-name:var(--ca-font)] text-ca-strong"
      >
        <span className="sr-only">Cargando tu evento...</span>
        <div aria-hidden="true" className="mx-auto flex w-full max-w-ca-reading-inset flex-col gap-4 px-5 py-6 pt-10 motion-safe:animate-pulse">
          <div className="h-7 w-40 rounded-ca-lg bg-ca-fill-2" />
          <div className="h-4 w-64 max-w-full rounded-ca-lg bg-ca-fill-2" />
          <div className="aspect-[2/1] w-full rounded-ca-lg bg-ca-fill-2" />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="size-11 shrink-0 rounded-ca-lg bg-ca-fill-2" />
              <div className="flex flex-1 flex-col gap-2">
                <div className="h-4 w-1/2 rounded-ca-lg bg-ca-fill-2" />
                <div className="h-3 w-1/3 rounded-ca-lg bg-ca-fill-2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div role="status" className="min-h-screen bg-background p-6">
      <span className="sr-only">Cargando tu evento...</span>
      <div aria-hidden="true" className="mx-auto flex max-w-3xl flex-col gap-4 motion-safe:animate-pulse">
        <div className="h-8 w-48 rounded-ca-md bg-muted" />
        <div className="h-4 w-72 max-w-full rounded-ca-md bg-muted" />
        <div className="aspect-[2/1] w-full rounded-ca-md bg-muted" />
        <div className="h-16 w-full rounded-ca-md bg-muted" />
        <div className="h-16 w-full rounded-ca-md bg-muted" />
      </div>
    </div>
  );
}
